import React, { useState } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Search, 
  Bell, 
  Settings, 
  Grid, 
  List, 
  MoreVertical, 
  ChevronLeft, 
  ChevronRight,
  LogOut,
  MessageCircle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

// Types pour les membres de l'équipe
interface TeamMember {
  id: string;
  name: string;
  email: string;
  avatar: string;
  role: string;
  progress: number;
}

// Types pour les projets
interface Project {
  id: string;
  name: string;
  status: 'completed' | 'in-progress' | 'waiting';
}

const Dashboard = () => {
  const { user, signOut } = useAuthContext();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedTeam, setSelectedTeam] = useState('Design Team');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Date actuelle formatée
  const currentDate = new Date().toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  // Données fictives pour les membres de l'équipe
  const teamMembers: TeamMember[] = [
    {
      id: '1',
      name: 'Henry Paulista',
      email: 'henry@mopaossi.com',
      avatar: '/avatars/henry.jpg',
      role: 'SENIOR CREATIVE DIRECTOR',
      progress: 100
    },
    {
      id: '2',
      name: 'Evan Jefferson',
      email: 'jefferson@mopaossi.com',
      avatar: '/avatars/evan.jpg',
      role: 'CREATIVE DIRECTOR',
      progress: 62
    },
    {
      id: '3',
      name: 'Mark Thomson',
      email: 'mark@mopaossi.com',
      avatar: '/avatars/mark.jpg',
      role: 'SENIOR UI DESIGNER',
      progress: 66
    },
    {
      id: '4',
      name: 'Alice McKenzie',
      email: 'alice.mck@mopaossi.com',
      avatar: '/avatars/alice.jpg',
      role: 'SENIOR COPYWRITER',
      progress: 100
    },
    {
      id: '5',
      name: 'Jack Ro',
      email: 'jack.ro@mopaossi.com',
      avatar: '/avatars/jack.jpg',
      role: 'ART DIRECTOR',
      progress: 33
    },
    {
      id: '6',
      name: 'Anastasia Groetze',
      email: 'anastasia@mopaossi.com',
      avatar: '/avatars/anastasia.jpg',
      role: 'SENIOR UX DESIGNER',
      progress: 48
    },
  ];

  // Données fictives pour les projets
  const projects = {
    total: 148,
    completed: 56,
    inProgress: 76,
    waiting: 16
  };

  // Filtrer les membres de l'équipe en fonction de la recherche
  const filteredMembers = teamMembers.filter(member => 
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSignOut = async () => {
    const { success } = await signOut();
    if (success) {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Tableau de bord | Mopaossi</title>
        <meta name="description" content="Votre tableau de bord personnel" />
      </Helmet>
      
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="w-16 md:w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 flex items-center justify-center md:justify-start">
            <div className="w-10 h-10 rounded-md bg-blue-500 flex items-center justify-center text-white">
              <span className="text-xl font-bold">M</span>
            </div>
            <span className="ml-2 text-xl font-semibold hidden md:block">Mopaossi</span>
          </div>
          
          <div className="mt-6 px-4">
            <Button variant="outline" className="w-full justify-start gap-2 hidden md:flex">
              <span>Demander</span>
            </Button>
            <Button variant="outline" className="w-10 h-10 p-0 flex items-center justify-center md:hidden">
              <span>+</span>
            </Button>
          </div>
          
          <nav className="mt-8 flex-1">
            <ul className="space-y-2">
              <li>
                <Button variant="ghost" className="w-full justify-start gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
                    <path d="M9 21V9" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="hidden md:inline">Dashboard</span>
                </Button>
              </li>
              <li>
                <Button variant="ghost" className="w-full justify-start gap-2 bg-blue-50 text-blue-600">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
                    <path d="M20 21C20 16.5817 16.4183 13 12 13C7.58172 13 4 16.5817 4 21" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="hidden md:inline">Personnes</span>
                </Button>
              </li>
              <li>
                <Button variant="ghost" className="w-full justify-start gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9H21M7 15H8M12 15H13M17 15H18M5 5H19C20.1046 5 21 5.89543 21 7V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V7C3 5.89543 3.89543 5 5 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className="hidden md:inline">Finances</span>
                </Button>
              </li>
              <li>
                <Button variant="ghost" className="w-full justify-start gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 5H21M3 12H21M3 19H21" stroke="currentColor" strokeWidth="2" />
                    <path d="M7 8L7 9M12 8V9M17 8V9M7 15V16M12 15V16M17 15V16" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="hidden md:inline">Statistiques</span>
                </Button>
              </li>
              <li>
                <Button variant="ghost" className="w-full justify-start gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7C21 5.89543 20.1046 5 19 5Z" stroke="currentColor" strokeWidth="2" />
                    <path d="M3 7L12 13L21 7" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="hidden md:inline">Documents</span>
                </Button>
              </li>
              <li>
                <Button variant="ghost" className="w-full justify-start gap-2">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 2V6M16 2V6M3 10H21M8 14H10M14 14H16M8 18H10M14 18H16" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="hidden md:inline">Calendrier</span>
                </Button>
              </li>
            </ul>
          </nav>
          
          <div className="mt-auto mb-4 px-4">
            <Button variant="ghost" className="w-full justify-start gap-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span className="hidden md:inline">Support</span>
            </Button>
            
            <Button variant="ghost" onClick={handleSignOut} className="w-full justify-start gap-2 mt-2">
              <LogOut className="w-5 h-5" />
              <span className="hidden md:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top header */}
          <header className="bg-white border-b border-gray-200 py-4 px-6 flex items-center justify-between">
            <div className="relative w-64 md:w-96">
              <Input 
                type="text" 
                placeholder="Rechercher..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-gray-600">{currentDate}</span>
              
              <div className="relative">
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="w-5 h-5" />
                  <span className="absolute top-0 right-0 w-2 h-2 bg-blue-500 rounded-full"></span>
                </Button>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src="/avatars/charles.jpg" alt="Charles Merl" />
                      <AvatarFallback>CM</AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline">Charles Merl</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>Profil</DropdownMenuItem>
                  <DropdownMenuItem>Paramètres</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut}>
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          
          {/* Main content area */}
          <main className="flex-1 overflow-auto p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
              <h1 className="text-2xl font-bold">Personnes</h1>
              
              <div className="flex items-center space-x-2 mt-4 md:mt-0">
                <Tabs defaultValue="all" className="w-full md:w-auto">
                  <TabsList>
                    <TabsTrigger value="all">Tous</TabsTrigger>
                    <TabsTrigger value="organization">Organisation</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Team members section */}
              <div className="md:col-span-2 bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2">
                          {selectedTeam}
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9L2 5H10L6 9Z" fill="currentColor" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => setSelectedTeam('Design Team')}>
                          Design Team
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedTeam('Development Team')}>
                          Development Team
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setSelectedTeam('Marketing Team')}>
                          Marketing Team
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2">
                          Position
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9L2 5H10L6 9Z" fill="currentColor" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem>Tous les postes</DropdownMenuItem>
                        <DropdownMenuItem>Designer</DropdownMenuItem>
                        <DropdownMenuItem>Développeur</DropdownMenuItem>
                        <DropdownMenuItem>Directeur</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="flex items-center gap-2">
                          Plus
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 9L2 5H10L6 9Z" fill="currentColor" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem>Filtrer par ancienneté</DropdownMenuItem>
                        <DropdownMenuItem>Filtrer par performance</DropdownMenuItem>
                        <DropdownMenuItem>Filtrer par disponibilité</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <Input 
                        type="text" 
                        placeholder="Rechercher par nom" 
                        className="pl-8 w-48"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    </div>
                    
                    <div className="flex border rounded-md">
                      <Button 
                        variant={viewMode === 'grid' ? 'default' : 'ghost'} 
                        size="icon" 
                        onClick={() => setViewMode('grid')}
                        className="rounded-r-none"
                      >
                        <Grid className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant={viewMode === 'list' ? 'default' : 'ghost'} 
                        size="icon" 
                        onClick={() => setViewMode('list')}
                        className="rounded-l-none"
                      >
                        <List className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-4`}>
                    {filteredMembers.map((member) => (
                      <div key={member.id} className="bg-white border rounded-lg p-4 relative">
                        <Button variant="ghost" size="icon" className="absolute top-2 right-2">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                        
                        <div className="flex flex-col items-center mb-4">
                          <div className="relative">
                            <Avatar className="w-16 h-16 mb-2">
                              <AvatarImage src={member.avatar} alt={member.name} />
                              <AvatarFallback>{member.name.charAt(0)}{member.name.split(' ')[1]?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                          </div>
                          <h3 className="font-semibold text-center">{member.name}</h3>
                          <p className="text-sm text-gray-500 text-center">{member.email}</p>
                        </div>
                        
                        <div className="mt-2">
                          <div className="flex justify-between items-center mb-1">
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full" 
                                style={{ width: `${member.progress}%` }}
                              ></div>
                            </div>
                            <span className="ml-2 text-xs text-gray-500">{member.progress}%</span>
                          </div>
                          <p className="text-xs text-gray-500 uppercase mt-2">{member.role}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="p-4 border-t border-gray-100 flex justify-between items-center">
                  <Button variant="ghost" size="sm" disabled>
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Précédent
                  </Button>
                  <div className="text-sm text-gray-500">
                    Page 1 sur 1
                  </div>
                  <Button variant="ghost" size="sm" disabled>
                    Suivant
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
              
              {/* Stats section */}
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-sm font-semibold text-gray-500">SÉLECTIONNÉ</h2>
                    <Button variant="ghost" size="icon">
                      <Settings className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-6">Design Team</h3>
                  
                  <div className="flex justify-center mb-4">
                    <div className="relative w-32 h-32">
                      <svg className="w-full h-full" viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="16" fill="none" stroke="#e6e6e6" strokeWidth="2"></circle>
                        <circle 
                          cx="18" cy="18" r="16" fill="none" stroke="#3b82f6" strokeWidth="2"
                          strokeDasharray={`${74 * 3.14 * 32 / 100} ${3.14 * 32}`}
                          strokeDashoffset="0"
                          transform="rotate(-90 18 18)"
                        ></circle>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <span className="text-3xl font-bold text-blue-500">74%</span>
                          <p className="text-xs text-gray-500">TAUX D'EXP.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow p-4">
                  <h2 className="text-xl font-bold mb-4">Projets</h2>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">TOTAL</span>
                      <span className="text-xl font-bold">{projects.total}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">COMPLÉTÉS</span>
                      <span className="text-xl font-bold text-green-500">{projects.completed}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">EN PROGRÈS</span>
                      <span className="text-xl font-bold text-blue-500">{projects.inProgress}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">EN ATTENTE</span>
                      <span className="text-xl font-bold text-yellow-500">{projects.waiting}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      
      {/* Fixed chat button */}
      <div className="fixed bottom-6 right-6 z-10">
        <div className="bg-blue-500 text-white rounded-full p-4 shadow-lg flex items-center">
          <Badge className="bg-green-500 absolute -top-1 -right-1">3</Badge>
          <MessageCircle className="w-6 h-6" />
          <span className="ml-2 font-medium">Messages internes</span>
          <ChevronRight className="w-4 h-4 ml-2" />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;